import { useMemo } from "react";
import {
  Image,
  ImageBackground,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { singleRankStyles } from "./styles";
import { IRanking, RankingType } from "./types";
import Centered from "../../../../shared/Centered";
import { inset } from "../../../../utils/helpers/css";
import { FormatWithThousandSeparator } from "../../../../utils/helpers/numberFormat";
import {
  FirstCup,
  PlayerLogo,
  Rank1Logo,
  Rank2Logo,
  Rank3Logo,
  RankBgr1,
  RankBgr2,
  RankBgr3,
  RankBgrRegular,
  RankChip,
  RankExp,
  RankLevelBorder,
  RankNameBorder,
  RankPlayerLogoBorder,
  RankStarLight,
} from "../../../../assets";
import { getAvatarUrl } from "../../../../utils/axiosInstance";

interface IProps {
  ranking: IRanking;
  rankingType: RankingType;
  isMobile: boolean;
  position: number;
}

function RankDataComponent({
  ranking,
  rankingType,
  isMobile,
  position,
}: IProps) {
  const rowHeight = useMemo(() => (isMobile ? 40 : 50), [isMobile]);
  const logoHeight = useMemo(() => (isMobile ? 44 : 54), [isMobile]);
  // const namePlateWidth = rowHeight * (isMobile ? 3 : 3);
  const namePlateWidth = useMemo(() => {
    if (rankingType === RankingType.Tournaments) {
      return rowHeight * (isMobile ? 2.4 : 2.7);
    }
    return rowHeight * 3;
  }, [rowHeight, isMobile, rankingType]);
  const namePlateHeight = rowHeight / 1.3;
  const namePlatePaddingX = isMobile ? 2 : 12;

  const helperData = useMemo(() => {
    const isInTopThree = position <= 3;

    const bgrSource =
      {
        1: RankBgr1,
        2: RankBgr2,
        3: RankBgr3,
      }[position] || RankBgrRegular;

    const rankSource =
      {
        1: Rank1Logo,
        2: Rank2Logo,
        3: Rank3Logo,
      }[position] || null;

    return {
      bgrSource,
      isInTopThree,
      bgrLeft: isInTopThree ? 20 : 0,
      showChips: rankingType !== RankingType.ExpTotal,
      showExp: rankingType === RankingType.ExpTotal,
      rankSource,
      showTournamentPoints: rankingType === RankingType.Tournaments,
    };
  }, [isMobile, rankingType]);

  const logoStyle = useMemo(() => {
    const heightCoef =
      {
        1: isMobile ? 1 : 1,
        2: isMobile ? 1.05 : 1.1,
        3: isMobile ? 1 : 1,
      }[position] || 1;

    const widthCoef =
      {
        1: isMobile ? 1.3 : 1.3 * 1.05,
        2: isMobile ? 1.2 : 1.2 * 1,
        3: isMobile ? 1.3 : 1.3 * 1.05,
      }[position] || 1;

    const marginTop =
      {
        1: isMobile ? 2 : 2,
        2: isMobile ? 4 : 5,
        3: isMobile ? 4 : 4,
      }[position] || 0;

    const logoHeight = rowHeight * heightCoef;
    const logoWidth = logoHeight * widthCoef;
    const marginLeft = position === 1 ? (isMobile ? -1 : 1) : 0;
    return StyleSheet.create({
      s: { width: logoWidth, height: logoHeight, marginTop, marginLeft },
    }).s;
  }, [isMobile]);

  const isWeb = Platform.OS === "web";
  const isNative = Platform.OS !== "web";

  return (
    <View style={[{ height: rowHeight }, singleRankStyles.container]}>
      {/* Background */}
      <View
        style={[
          singleRankStyles.backgroundWrapper,
          { left: helperData.bgrLeft },
        ]}
      >
        <Image
          source={helperData.bgrSource}
          resizeMode="cover"
          style={singleRankStyles.background}
        />
      </View>

      <View style={{ flex: 1, flexDirection: "row" }}>
        {/* Ranking Logo */}
        <Centered
          style={{ height: rowHeight, width: rowHeight, position: "relative" }}
        >
          {helperData.isInTopThree && (
            <Image source={helperData.rankSource} style={logoStyle} />
          )}

          <Centered
            style={[
              singleRankStyles.rankLogo,
              { top: helperData.isInTopThree ? -1 : 0 },
              { bottom: helperData.isInTopThree ? 8 : 0 },
            ]}
          >
            <Text
              style={[singleRankStyles.text, { fontSize: isMobile ? 14 : 18 }]}
              children={position}
            />
          </Centered>
        </Centered>
        {/* Player Logo */}
        <Centered
          style={{
            marginLeft: isMobile ? -8 : 16,
            height: rowHeight,
            width: rowHeight,
            position: "relative",
          }}
        >
          <Image
            source={
              ranking?.avatarFileId
                ? { uri: getAvatarUrl(ranking.avatarFileId) }
                : PlayerLogo
            }
            style={{
              height: logoHeight - 6,
              width: logoHeight - 6,
              borderRadius: (logoHeight - 6) / 2,
            }}
          />
          {Platform.OS === "web" && (
            <Image
              source={RankPlayerLogoBorder}
              style={{
                position: "absolute",
                ...inset(-0.5 * (logoHeight - rowHeight)),
              }}
            />
          )}
          <Image
            source={RankStarLight}
            style={{
              position: "absolute",
              left: isMobile ? 5 : 6,
              top: isMobile ? -6 : -8,
              height: isMobile ? 24 : 30,
              width: isMobile ? 24 : 30,
            }}
          />

          <Centered
            style={{
              position: "absolute",

              ...(isWeb
                ? {
                    top: "50%",
                    transform: [{ translateY: -rowHeight / 1.7 / 2 }],
                    width: rowHeight / 1.7,
                    height: rowHeight / 1.7,
                    right: isMobile ? -17 : -22,
                  }
                : {
                    top: 12,
                    width: 10,
                    height: 10,
                    right: -3,
                  }),
            }}
          >
            <Image
              source={RankLevelBorder}
              resizeMode="contain"
              style={{
                ...(isWeb ? { ...singleRankStyles.fullAbsoluteInset } : {}),
                ...(isWeb
                  ? {}
                  : { width: 20, height: 20, position: "absolute", left: 0 }),
              }}
            />
            <Text
              style={[
                singleRankStyles.text,
                {
                  fontSize: isMobile ? 12 : 14,
                  ...(isWeb
                    ? { position: "relative" }
                    : {
                        lineHeight: (isMobile ? 12 : 14) + 2,
                        textAlignVertical: "center",
                        marginLeft: 6,
                      }),
                },
              ]}
              children={ranking.level}
            />
          </Centered>
        </Centered>

        {/* Username */}
        <View style={{ flex: 1, alignItems: "center" }}>
          <Centered
            style={{
              marginLeft: 10,
              height: rowHeight,
              width: namePlateWidth,
            }}
          >
            <ImageBackground
              source={RankNameBorder}
              resizeMode="contain"
              style={{
                width: namePlateWidth,
                height: namePlateHeight,
                justifyContent: "center",
                alignItems: "center",
              }}
              imageStyle={{
                width: namePlateWidth,
                height: namePlateHeight,
              }}
            >
              <Text
                style={[
                  singleRankStyles.text,
                  {
                    fontSize: isMobile ? 12 : 14,
                    maxWidth: namePlateWidth - namePlatePaddingX * 2,
                    textAlign: "center",
                  },
                ]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {ranking.username}
              </Text>
            </ImageBackground>
          </Centered>
        </View>
      </View>

      {helperData.showTournamentPoints && (
        <Centered
          style={{
            height: rowHeight,
            width: isMobile ? 78 : 110,
            marginLeft: isMobile ? 4 : 10,
            flexDirection: "row",
          }}
        >
          <Image
            source={FirstCup}
            resizeMode="contain"
            style={{
              width: isMobile ? 20 : 22,
              height: isMobile ? 24 : 22,
              marginRight: 4,
            }}
          />
          <Text
            style={[
              singleRankStyles.text,
              {
                fontSize: isMobile ? 15 : 16,
              },
            ]}
            numberOfLines={1}
          >
            {FormatWithThousandSeparator(ranking.weeklyTournamentPoints ?? 0, {
              fractionDigits: 0,
            })}
          </Text>
        </Centered>
      )}

      {/* Chips, Exps */}
      <Centered style={singleRankStyles.chipExpContainer}>
        {!helperData.showExp && (
          <Image
            source={RankChip}
            resizeMode="stretch"
            style={{
              height: rowHeight / (isMobile ? 1.8 : 1.6),
              width: rowHeight / (isMobile ? 1.7 : 1.6),
            }}
          />
        )}
        {helperData.showExp && (
          <Image
            source={RankExp}
            resizeMode="stretch"
            style={{ height: rowHeight / 1.5, width: rowHeight / 1.6 }}
          />
        )}

        <Text
          style={[singleRankStyles.text, { fontSize: isMobile ? 15 : 16 }]}
          children={FormatWithThousandSeparator(
            helperData.showExp ? ranking.exp : ranking.totalChips,
            { fractionDigits: 0 },
          )}
        ></Text>
      </Centered>
    </View>
  );
}

export default RankDataComponent;
